import { Component, OnInit, ViewChild, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { CardOrderingService, CardOrderingDetailsResponse } from '@src/app/shared/backend/clients';
import { CardOrderingItemStatus } from '@app/shared/enums/CardOrderingItemStatus.enum';
import { CardOrderingStatus } from '@src/app/shared/enums/CardOrderingStatus.enum';
import { CardOrderingStatusPipe } from '@app/shared/pipes/cardOrderingStatus.pipe';
import { PermissionService } from '@src/app/shared/services/permission.service';
import { Actions } from '@src/app/shared/backend/auth/Action';
import { DialogReasonComponent } from '@src/app/shared/components/dialog-reason/dialog-reason.component';
import { ConfirmationService } from 'primeng/api';
import { BaseViewComponent } from '@src/app/shared/components/base-list/base-view.component';

interface CardOrderItem {
  itemId: number | null;
  serialNumber: string;
  retailerId: number | null;
  retailerName: string;
  status: string;
  id: number;
}

interface GroupedRetailerItems {
  retailerId: number | null;
  retailerName: string;
  items: CardOrderItem[];
  paginatorFirst: number;
  paginatorRows: number;
}

@Component({
    selector: 'app-view-card-order',
    templateUrl: './view-card-order.component.html',
    styleUrl: './view-card-order.component.scss',
    host: {
        class: 'app-view-card-order'
    },
    providers: [CardOrderingStatusPipe, ConfirmationService],
    standalone: false
})
export class ViewCardOrderComponent extends BaseViewComponent implements OnInit, OnDestroy {

  private readonly destroyedComponent$ = new Subject<void>();

  @ViewChild('dialog') dialog: DialogReasonComponent | undefined;

  delete: boolean = false;
  allowViewRetailer: boolean = false;
  allowViewUser: boolean = false;
  cardOrderId: number = 0;
  cardOrderData: CardOrderingDetailsResponse = {
    id: 0,
    retailerId: 0,
    retailerName: '',
    retailerCode: '',
    retailerAddress: '',
    createdById: '',
    createdByFullName: '',
    createdOn: '',
    cardOrderingStatus: 'CREATED'
  };
  cardOrderingStatus = CardOrderingStatus;
  cardOrderItemState = CardOrderingItemStatus;
  displayAddItemsModalbox: boolean = false;
  displayRemoveItemConfirmationModalbox: boolean = false;
  selectedItemSerialNumber: string | undefined;

  // local pagination
  groupedRetailerItems: GroupedRetailerItems[] = [];
  rowsPerPageOptions = [10, 20, 30];
  defaultRows = 10;

  addItemsFormGroup: FormGroup = this.fb.group({
    cardOrderItems: [[], Validators.required],
  });

  constructor(
    private permissionService: PermissionService,
    private router: Router,
    private route: ActivatedRoute,
    private cardOrderingService: CardOrderingService,
    private changeDetector: ChangeDetectorRef,
    private fb: FormBuilder) { super(); }

  ngOnInit() {
    this.init();
  }

  private async init(): Promise<void> {
    this.route.params
      .pipe(takeUntil(this.destroyedComponent$))
      .subscribe(async (params) => {
        this.cardOrderId = Number(params['cardOrderId']);
        if (this.cardOrderId > 0) {
          await this.get();
        }
      });

    this.delete = await this.permissionService.hasActionPermissions(Actions.delete);
    this.allowViewRetailer = await this.permissionService.hasActionPermissions(Actions.view_retailers);
    this.allowViewUser = await this.permissionService.hasActionPermissions(Actions.view_users);
  }

  override async get() {
    try {
      this.cardOrderData = await firstValueFrom(this.cardOrderingService.apiCardOrderingDetailsIdGet(this.cardOrderId));
      this.buildGroupedRetailerItems();
    } catch (error) {
      console.error('Error fetching card order data:', error);
    } finally {
      this.changeDetector.detectChanges();
    }
  }

  private buildGroupedRetailerItems() {
    const items = (this.cardOrderData?.cardOrderingItems ?? []) as CardOrderItem[];

    const groupedMap = new Map<number | null, GroupedRetailerItems>();

    for (const item of items) {
      const retailerKey = item.retailerId ?? null;

      if (!groupedMap.has(retailerKey)) {
        groupedMap.set(retailerKey, {
          retailerId: item.retailerId,
          retailerName: item.retailerName,
          items: [],
          paginatorFirst: 0,
          paginatorRows: this.defaultRows
        });
      }

      groupedMap.get(retailerKey)!.items.push(item);
    }

    this.groupedRetailerItems = Array.from(groupedMap.values());
  }

  getPaginatedItems(group: GroupedRetailerItems): CardOrderItem[] {
    const startIndex = group.paginatorFirst;
    const endIndex = startIndex + group.paginatorRows;

    return group.items.slice(startIndex, endIndex);
  }

  getGroupPaginatorPageNumber(group: GroupedRetailerItems): number {
    const rows = group.paginatorRows || this.defaultRows || 1;

    // convert local 0-based item offset to the 1-based paginator expected page number
    return Math.floor((group.paginatorFirst ?? 0) / rows) + 1;
  }

  onGroupPageChange(event: any, group: GroupedRetailerItems) {
    group.paginatorFirst = event.first;
    group.paginatorRows = event.rows;
  }

  trackByRetailerGroup(index: number, group: GroupedRetailerItems) {
    return group.retailerId;
  }

  trackByCardOrderItemId(index: number, item: CardOrderItem) {
    return item.id;
  }

  async showDeleteDialog(): Promise<void> {
    const deleteCheckResponses = await firstValueFrom(this.cardOrderingService.apiCardOrderingDeleteCheckBatchPost([this.cardOrderId]));

    this.dialog?.show(deleteCheckResponses);
  }

  async deleteCardOrder(doDelete: boolean): Promise<void> {
    if (doDelete) {
      await firstValueFrom(this.cardOrderingService.apiCardOrderingDeleteIdDelete(this.cardOrderId));
      this.router.navigate([`/card-orders`]);
    }
  }

  ngOnDestroy(): void {
    this.destroyedComponent$.next();
    this.destroyedComponent$.complete();
  }
}
